
import React, { useEffect } from 'react';
import { LanguageMode } from './types/audioTypes';
import { validateLanguageContent, getLanguageModeLabel } from '@/utils/languageValidation';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, AlertCircle } from 'lucide-react';

interface LanguageSelectorProps {
  selectedLanguage: LanguageMode;
  onLanguageChange: (language: LanguageMode) => void;
  variant?: 'default' | 'golden' | 'compact';
  disabled?: boolean;
  contentText?: string; // For content validation
}

const LANGUAGE_OPTIONS: { value: LanguageMode; label: string; description: string; shortLabel: string }[] = [
  { value: 'english', label: 'English Only', description: 'English text only', shortLabel: 'EN' },
  { value: 'chinese', label: 'Chinese Only', description: 'Chinese text only', shortLabel: '中文' },
  { value: 'both', label: 'Both Languages', description: 'All text content', shortLabel: 'Both' },
];

export function LanguageSelector({
  selectedLanguage,
  onLanguageChange,
  variant = 'default',
  disabled = false,
  contentText
}: LanguageSelectorProps) {
  const { toast } = useToast();
  const isGolden = variant === 'golden';
  const isCompact = variant === 'compact';
  
  // Validate current selection if content is provided
  const validation = contentText ? validateLanguageContent(contentText, selectedLanguage) : { isValid: true };
  const hasValidContent = validation.isValid;

  // Show toast when validation fails
  useEffect(() => {
    if (contentText && !validation.isValid && validation.message) {
      const currentModeLabel = getLanguageModeLabel(selectedLanguage);
      const suggestedModeLabel = validation.suggestedMode ? getLanguageModeLabel(validation.suggestedMode) : null;

      toast({
        title: `${currentModeLabel} Content Not Available`,
        description: suggestedModeLabel 
          ? `${validation.message} Try switching to "${suggestedModeLabel}" mode.`
          : validation.message,
        variant: 'destructive'
      });
    }
  }, [selectedLanguage, contentText, validation, toast]);

  const currentOption = LANGUAGE_OPTIONS.find(option => option.value === selectedLanguage);

  // Compact variant (dropdown style)
  if (isCompact) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            disabled={disabled}
            className={`flex items-center gap-1 p-2 hover:bg-white/10 dark:hover:bg-black/20 transition-colors rounded-lg text-xs font-medium min-w-[50px] justify-center ${
              hasValidContent 
                ? 'text-gray-600 dark:text-gray-400' 
                : 'text-red-600 dark:text-red-400'
            }`}
            aria-label={`Language filter: ${currentOption?.label || 'Both'}${!hasValidContent ? ' (No content available)' : ''}`}
          >
            <span>{currentOption?.shortLabel || 'Both'}</span>
            {!hasValidContent && <AlertCircle size={10} />}
            <ChevronDown size={12} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="center" 
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-50"
        >
          {LANGUAGE_OPTIONS.map(({ value, label, shortLabel }) => {
            const optionValidation = contentText ? validateLanguageContent(contentText, value) : { isValid: true };
            const isOptionValid = optionValidation.isValid;
            
            return (
              <DropdownMenuItem
                key={value}
                onClick={() => onLanguageChange(value)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  selectedLanguage === value
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                    : isOptionValid
                    ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    : 'text-gray-400 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center justify-between w-full">
                  <span className="flex items-center gap-2">
                    {shortLabel}
                    {!isOptionValid && <AlertCircle size={12} className="text-red-500" />}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{label}</span>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant (select dropdown)
  const selectClass = isGolden 
    ? 'bg-black/30 border-gold/30 text-gold' 
    : 'bg-gray-800 border-white/20 text-white';

  return (
    <div className="space-y-2">
      <select
        value={selectedLanguage}
        onChange={(e) => onLanguageChange(e.target.value as LanguageMode)}
        disabled={disabled}
        className={`w-full px-3 py-2 rounded-lg border text-sm ${selectClass} focus:outline-none focus:ring-2 focus:ring-primary/50 ${
          !hasValidContent ? 'border-red-500' : ''
        }`}
      >
        {LANGUAGE_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label} - {option.description}
          </option>
        ))}
      </select>
      {!hasValidContent && validation.message && (
        <div className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle size={12} />
          {validation.message}
        </div>
      )}
    </div>
  );
}
